import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Amplify } from 'aws-amplify'
import toast, { Toaster } from 'react-hot-toast'
import '../index.css'

import App from './App'
import { amplifyConfig } from '@/config/amplify'
import { createApiRetryHandler } from '@/utils/apiRetry'
import '@/styles/globals.css'

// Initialize Sentry before everything else
import { initializeSentry } from '@/config/sentry'
initializeSentry()

// Initialize Firebase
import { initializeFirebase } from '@/config/firebase'
initializeFirebase()

// Initialize Microsoft Clarity
import { initializeClarity } from '@/config/clarity'
initializeClarity()

// Initialize global error handling
import '@/utils/errorHandling'

// Configure Amplify
Amplify.configure(amplifyConfig)

// Create QueryClient with optimized defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: createApiRetryHandler(3),
    },
    mutations: {
      retry: createApiRetryHandler(3),
    },
  },
})

const root = ReactDOM.createRoot(document.getElementById('root')!)

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
        <Toaster
          position="bottom-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#254769',
              color: '#fff',
              cursor: 'pointer',
            },
          }}
        >
          {t => (
            <div
              onClick={() => toast.dismiss(t.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                background: '#254769',
                color: '#fff',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              {t.icon && <div>{t.icon}</div>}
              <div style={{ flex: 1 }}>
                {typeof t.message === 'function' ? t.message(t) : t.message}
              </div>
              <button
                onClick={e => {
                  e.stopPropagation()
                  toast.dismiss(t.id)
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '20px',
                  padding: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '20px',
                  height: '20px',
                  opacity: 0.8,
                  transition: 'opacity 0.2s',
                }}
                onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                onMouseLeave={e => (e.currentTarget.style.opacity = '0.8')}
                aria-label="Close notification"
              >
                ×
              </button>
            </div>
          )}
        </Toaster>
      </BrowserRouter>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </React.StrictMode>
)
